import moment from "moment";
import { paramsListName, siteUrl } from "src/api/config";
import { EmailListFieldsRecordType, emailQueueListName } from "src/api/config/email.config";
import { Option } from "src/components/ui/TGSelect";
import { addItemRequest, deleteItemRequest, getItemsRequest } from "src/modules/spItems/api";
import { removeInvisibleChar } from "./string.utils";
import { prefix, removePrefix } from "./users.utils";
import qs from "qs";
import { isStrapi } from "src/api/config/systemSwitch";
import { mode } from "src/constants";
import { BACKEND_URL, STRAPI_TOKEN } from "src/pages/DashboardPage/gop/api/config";
import { UserRecord } from "src/types/main";

type TemplateProps = {
  header: string;
  sent?: Date;
  subject: string;
  user: { name: string; email: string };
  document?: {
    title: string;
    lists: { title: string; link: string }[];
  };
  periodDate?: {
    title: string;
    date: Date;
    expired?: boolean;
  };
};

export const getUrlWithoutAttr = (attr: string) => window.location.href.slice(0, window.location.href.indexOf(attr));

export function emailAPI() {
  function createEmailBodyByTemplate({
    header,
    sent = new Date(),
    subject,
    user,
    document,
    periodDate,
  }: TemplateProps) {
    return `Здравствуйте ${user.name},
    <br/><br/>
      ${
        document
          ? `
          <p>${document.title}:</p>
            ${document.lists
              .map((listItem) => `<a href=${listItem.link}>` + listItem.title + "</a><br/><br/>")
              .join("")}

        `
          : ""
      }
        ${
          periodDate
            ? `<p>${periodDate.title}: <span${periodDate.expired ? ' style="color:red;"' : ""}>${
                moment(periodDate.date).format("DD.MM.YYYY") /* пример: дд.мм.гггг */
              }</span></p>`
            : ""
        }
    `;
  }

  function createEmailMeetingBodyByTemplate() {
    return {
      archiveMeeting: (
        meeting: { title: string; id: string | number },
        creator: string,
        userName: string,
      ) => `Здравствуйте ${userName}, <br/><br/>
      Примите к сведению, что сотрудник ${creator} архивировал совещание <a href="${
        siteUrl + "/dashboard/3/meetings?meetingCard=" + meeting.id.toString()
      }">${meeting.title}</a><br/><br/>.
    `,
      createMeeting: (
        meeting: { title: string; id: string | number },
        creator: string,
        userName: string,
      ) => `Здравствуйте ${userName}, <br/><br/>
      Сотрудник ${creator} создал новое совещание <a href="${
        siteUrl + "/dashboard/3/meetings?meetingCard=" + meeting.id.toString()
      }">${meeting.title}</a><br/><br/> и назначил Вас его участником.
    `,
      quitParticipantFromMeeting: (
        meeting: { title: string; id: string | number },
        creator: string,
        userName: string,
      ) => `Здравствуйте ${userName}, <br/><br/>
      Примите к сведению, что сотрудник ${creator} вышел из совещания <a href="${
        siteUrl + "/dashboard/3/meetings?meetingCard=" + meeting.id.toString()
      }">${meeting.title}</a><br/><br/>.
    `,
      inviteParticipantToMeeting: (
        meeting: { title: string; id: string | number },
        creator: string,
        userName: string,
      ) => `Здравствуйте ${userName}, <br/><br/>
      Примите к сведению, что сотрудник ${creator} добавил Вас в список участников совещания <a href="${
        siteUrl + "/dashboard/3/meetings?meetingCard=" + meeting.id.toString()
      }">${meeting.title}</a><br/><br/>.
    `,
      assigningOwnerInMeeting: (
        meeting: { title: string; id: string | number },
        creator: string,
        userName: string,
      ) => `Здравствуйте ${userName}, <br/><br/>
      Примите к сведению, что сотрудник ${creator} назначил Вас владельцем за совещание  <a href="${
        siteUrl + "/dashboard/3/meetings?meetingCard=" + meeting.id.toString()
      }">${meeting.title}</a><br/><br/>.
    `,
    };
  }

  return {
    sendEmail: async (body: Partial<EmailListFieldsRecordType[typeof emailQueueListName]>): Promise<number> => {
      return (await addItemRequest({
        listName: emailQueueListName,
        body: body,
        updateReducer: false,
      })) as number;
    },
    sendEmailStrapi: async (body: Partial<EmailListFieldsRecordType[typeof emailQueueListName]>): Promise<Response> => {
      const payload = { data: body };
      return await fetch(`${BACKEND_URL}/api/sp-queued-emails`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${STRAPI_TOKEN}`,
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify(payload),
      });
    },
    deleteEmail: async (IDs: { ID: number }[]): Promise<number> => {
      return (await deleteItemRequest({
        listName: emailQueueListName,
        ID: 0,
        Ids: IDs,
        updateReducer: false,
      })) as number;
    },
    createEmailBodyByTemplate,
    createEmailMeetingBodyByTemplate,
  };
}

type FetchOptions = RequestInit;
type FetchError = {
  message: string;
  status?: number;
  statusText?: string;
};

type FetchResult<T> = {
  data: T | null;
  error: FetchError | null;
  loading: boolean;
};

type Interceptor = (input: string, init: RequestInit) => Promise<[string, RequestInit]>;
type ResponseInterceptor = (response: Response) => Promise<Response>;

async function createFetchWithInterceptors<T>(
  fetchFunction: () => Promise<[string, FetchOptions]>,
  requestInterceptors: Interceptor[] = [],
  responseInterceptors: ResponseInterceptor[] = [],
  customErrorHandler?: (error: FetchError) => void,
): Promise<FetchResult<T>> {
  const result: FetchResult<T> = {
    data: null,
    error: null,
    loading: true,
  };

  try {
    // Get initial URL and options from fetchFunction
    let [url, options] = await fetchFunction();

    // Apply request interceptors
    for (const interceptor of requestInterceptors) {
      [url, options] = await interceptor(url, options);
    }

    let response = await fetch(url, options);

    // Apply response interceptors
    for (const interceptor of responseInterceptors) {
      response = await interceptor(response);
    }

    if (!response.ok) {
      const error: FetchError = {
        message: `HTTP error! status: ${response.status}`,
        status: response.status,
        statusText: response.statusText,
      };
      throw error;
    }

    result.data = await response.json();
  } catch (error) {
    const fetchError = error as FetchError;
    if (customErrorHandler) {
      customErrorHandler(fetchError);
    } else {
      result.error = fetchError;
    }
  } finally {
    result.loading = false;
  }

  return result;
}

export default createFetchWithInterceptors;

// Request interceptor example
const requestInterceptor = async (url: RequestInfo, options?: RequestInit): Promise<[RequestInfo, RequestInit?]> => {
  console.log("Request intercepted:", url, options);
  // Modify request options if needed
  return [url, options];
};

// Response interceptor example
const responseInterceptor = async (response: Response): Promise<Response> => {
  console.log("Response intercepted:", response);
  // Modify response if needed
  return response;
};

export const buildSearchQuery = (
  name: string,
  departments: string[],
  jobTitles: string[],
  locations: string[],
  groups: string[],
  subordinates: Option[],
  // workplaces: Option[],
) => {
  if (isStrapi) {
    const filters = [];
    if (name) {
      filters.push({
        $or: [
          {
            Name: {
              $containsi: name || "Какая-то несуществующая строка, чтобы избежать ошибки",
            },
          },
          {
            PersonalNumber: {
              $containsi: name || "Какая-то несуществующая строка, чтобы избежать ошибки",
            },
          },
        ],
      });
    }
    if (groups.length > 0) {
      const cleanedGroups = groups.map((email: string) => removePrefix(email.toLowerCase().trim(), prefix));
      filters.push({ email: { $in: cleanedGroups } });
    }

    // if (workplaces.length > 0) {
    //   const cleanedWorkplacesNested = workplaces.map((workplace: Option) =>
    //     workplace.value.User.map((user: UserRecord) => removePrefix(user.Email.toLowerCase().trim(), prefix)),
    //   );

    //   const cleanedWorkplaces = cleanedWorkplacesNested.flat();
    //   if (cleanedWorkplaces.length > 0) {
    //     filters.push({ email: { $in: cleanedWorkplaces } });
    //   }
    // }

    if (subordinates.length > 0) {
      let emails: string[] = [];
      for (const group of subordinates) {
        const cleanedGroups = group.value.DirectReports.map((email: string) =>
          removePrefix(email.toLowerCase().trim(), prefix),
        );
        if (cleanedGroups.length > 0) {
          emails = [...emails, ...cleanedGroups];
        }
      }
      if (emails.length > 0) {
        filters.push({ email: { $in: emails } });
      }
    }
    if (departments.length > 0) {
      filters.push({
        $or: departments.map((val) => ({
          Department: {
            $containsi: val || "Какая-то несуществующая строка, чтобы избежать ошибки",
          },
        })),
      });
    }
    if (jobTitles.length > 0) {
      filters.push({
        $or: jobTitles.map((val) => ({
          Position: {
            $containsi: val || "Какая-то несуществующая строка, чтобы избежать ошибки",
          },
        })),
      });
    }
    if (locations.length > 0) {
      filters.push({
        $or: locations.map((val) => ({
          Location: {
            $containsi: val || "Какая-то несуществующая строка, чтобы избежать ошибки",
          },
        })),
      });
    }

    const query = qs.stringify(
      {
        pagination: {
          pageSize: 150,
          page: 1,
        },
        filters: { $and: filters, blocked: false },
        fields: ["id", "username", "email", "PersonalNumber", "City", "Name", "Position", "Location", "Department"],
        populate: {
          PictureUrl: {
            // Получаем менеджера со всеми полями
            populate: "*",
          },
        },
      },
      { encodeValuesOnly: true }, // Чтобы строка была читаемой
    );
    return query;
  } else {
    let queryParts = [];

    if (groups.length > 0) {
      let queryGroups = [];

      const cleanedGroups = groups.map((email: string) => removePrefix(email, prefix));

      if (cleanedGroups.length > 0) {
        const reducedGroup = cleanedGroups.reduce((acc: string[], group: string) => {
          acc.push("(AccountName:" + group + "* OR WorkEmail:" + group + "*)");

          return acc;
        }, [] as string[]);
        const queryText = "(" + reducedGroup.join(" OR ") + ")";
        queryGroups.push(queryText);
      }

      queryParts.push(queryGroups.join(" OR "));
    }

    if (workplaces.length > 0) {
      let queryGroups = [];

      const cleanedWorkplaces = workplaces.map((workplace: Option) =>
        workplace.value.User.map((user: UserRecord) => removePrefix(user.Email.toLowerCase().trim(), prefix)),
      );

      if (cleanedWorkplaces.length > 0) {
        const reducedWorkplaces = cleanedWorkplaces.reduce((acc: string[], workplace: string) => {
          acc.push("(AccountName:" + workplace + "* OR WorkEmail:" + workplace + "*)");

          return acc;
        }, [] as string[]);
        const queryText = "(" + reducedWorkplaces.join(" OR ") + ")";
        queryGroups.push(queryText);
      }

      queryParts.push(queryGroups.join(" OR "));
    }

    if (subordinates.length > 0) {
      let querySubordinates = [];

      for (const group of subordinates) {
        const cleanedGroups = group.value.DirectReports.map((email: string) => removePrefix(email, prefix));

        if (cleanedGroups.length > 0) {
          const reducedGroup = cleanedGroups.reduce((acc: string[], group: string) => {
            acc.push("(AccountName:" + group + "* OR WorkEmail:" + group + "*)");

            return acc;
          }, [] as string[]);
          const queryText = "(" + reducedGroup.join(" OR ") + ")";
          querySubordinates.push(queryText);
        }
      }

      queryParts.push(querySubordinates.join(" OR "));
    }

    if (name.length > 0) {
      const nameQuery = `(LastName:"${name}*" OR FirstName:"${name}*" OR PreferredName:"${name}*" OR apiGlobalID:"${name}*" OR abiGlobalID:"${name}*" OR UserName:"${name}*")`;

      queryParts.push(nameQuery);
    }

    if (departments.length > 0) {
      const departmentQuery = departments.map((dep) => `(DepartmentRUS:"${dep}" OR Department:"${dep}")`).join(" OR ");
      queryParts.push(`(${departmentQuery})`);
    }

    if (jobTitles.length > 0) {
      const jobTitleQuery = jobTitles.map((job) => `(JobTitle:"${job}" OR TitleRUS:"${job}")`).join(" OR ");
      queryParts.push(`(${jobTitleQuery})`);
    }

    if (locations.length > 0) {
      const locationQuery = locations
        .map((loc) => `(Region:"${loc}" OR BaseOfficeLocation:"${loc}" OR SPS-Location:"${loc}")`)
        .join(" OR ");
      queryParts.push(`(${locationQuery})`);
    }

    return queryParts.join(" AND ");
  }
};

export const handleLogout = async () => {
  localStorage.clear();
  window.location.href = "https://login.microsoftonline.com/common/oauth2/v2.0/logout";
};

export async function getParamsByKey(key: string) {
  const { items } = await getItemsRequest({
    listName: paramsListName,
    filters: { Key: key },
  });

  const stringWithoutInvisibleChar = removeInvisibleChar(items[0].Value); // minutes

  if (!stringWithoutInvisibleChar) {
    return "";
  }

  return stringWithoutInvisibleChar;
}
